import { Avatar, InputAdornment, SxProps, Theme } from '@mui/material';
import { SelfServiceReferralsSearchIcon } from 'Images';
import { ReactNode } from 'react';
import { SearchFieldClearEndAdornment } from './SearchFieldClearEndAdornment';

export {
  SearchFieldClearEndAdornment,
  SearchFieldClearIconButton,
} from './SearchFieldClearEndAdornment';

export const SEARCH_FIELD_ICON_SIZE = 17;

export const SearchFieldSearchIconGraphic = () => (
  <Avatar
    src={SelfServiceReferralsSearchIcon}
    alt=''
    aria-hidden
    sx={{
      width: SEARCH_FIELD_ICON_SIZE,
      height: SEARCH_FIELD_ICON_SIZE,
      pointerEvents: 'none',
      '& img': { objectFit: 'contain' },
    }}
  />
);

export const SearchFieldSearchStartAdornment = () => (
  <InputAdornment position='start' sx={{ margin: 0, maxHeight: 'unset' }}>
    <SearchFieldSearchIconGraphic />
  </InputAdornment>
);

interface ISearchFieldSearchIconButtonProps {
  ariaLabel: string;
  onActivate: () => void;
  sx?: SxProps<Theme>;
}

export const SearchFieldSearchIconButton = ({
  ariaLabel,
  onActivate,
  sx,
}: ISearchFieldSearchIconButtonProps) => (
  <Avatar
    src={SelfServiceReferralsSearchIcon}
    alt=''
    role='button'
    aria-label={ariaLabel}
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    }}
    onClick={onActivate}
    sx={{
      flexShrink: 0,
      width: SEARCH_FIELD_ICON_SIZE,
      height: SEARCH_FIELD_ICON_SIZE,
      cursor: 'pointer',
      '& img': { objectFit: 'contain' },
      '&:focus-visible': { outline: '3px solid #1525E8', outlineOffset: 2 },
      ...sx,
    }}
  />
);

export const SearchFieldSearchStartAdornmentButton = ({
  ariaLabel,
  onActivate,
  sx,
}: ISearchFieldSearchIconButtonProps) => (
  <InputAdornment
    position='start'
    sx={{ margin: 0, maxHeight: 'unset', ...sx }}
  >
    <SearchFieldSearchIconButton
      ariaLabel={ariaLabel}
      onActivate={onActivate}
    />
  </InputAdornment>
);

export const searchFieldOutlinedInputRootSx = () => ({
  direction: 'rtl' as const,
  gap: '8px',
  px: '16px',
});

export const searchFieldTriggerRowSx = {
  direction: 'rtl' as const,
  gap: '8px',
  px: '16px',
} as const;

export type SearchFieldIconMode = 'decorative' | 'button';

interface IBuildSearchFieldAdornmentsOptions {
  hasValue: boolean;
  clearAriaLabel: string;
  onClear: () => void;
  searchIconMode?: SearchFieldIconMode;
  searchAriaLabel?: string;
  onSearchActivate?: () => void;
  clearSize?: number | string;
  onClearMouseDown?: (event: React.MouseEvent) => void;
  onClearClick?: (event: React.MouseEvent) => void;
}

export interface ISearchFieldAdornments {
  startAdornment: ReactNode;
  endAdornment: ReactNode | undefined;
}

export const buildSearchFieldAdornments = ({
  hasValue,
  clearAriaLabel,
  onClear,
  searchIconMode = 'decorative',
  searchAriaLabel = '',
  onSearchActivate,
  clearSize,
  onClearMouseDown,
  onClearClick,
}: IBuildSearchFieldAdornmentsOptions): ISearchFieldAdornments => {
  const startAdornment =
    searchIconMode === 'button' && onSearchActivate ? (
      <SearchFieldSearchStartAdornmentButton
        ariaLabel={searchAriaLabel}
        onActivate={onSearchActivate}
      />
    ) : (
      <SearchFieldSearchStartAdornment />
    );

  const endAdornment = hasValue ? (
    <SearchFieldClearEndAdornment
      ariaLabel={clearAriaLabel}
      onClear={onClear}
      size={clearSize}
      onMouseDown={onClearMouseDown}
      onClick={onClearClick}
    />
  ) : undefined;

  return { startAdornment, endAdornment };
};
